import React from "react"
import { Check } from "lucide-react"
import { money } from "../home/format"

// Presentational pricing card (doc 10), shared by the public and in-app pages.
// `cta` is the button slot — public renders a waitlist button, in-app renders a
// checkout button — so the card body stays identical across both.
const unl = (copy) => <span className="font-semibold text-brand-500 dark:text-brand-400">{copy.limits.unlimited}</span>
const check = <Check className="h-4 w-4 text-brand-500 dark:text-brand-400" />

// Numeric rows differentiate the tiers; feature rows are the product surfaces we
// actually ship (payments ledger, subscription/CRM/traffic reports) — included
// on every plan, so they render as checks.
function limitRows(plan, copy) {
  const u = copy.units
  const l = plan.limits
  const n = (v) => <span className="font-semibold text-gray-900 dark:text-gray-100">{v}</span>
  return [
    [copy.limits.mtr, l.mtr_cents == null ? unl(copy) : n(money(l.mtr_cents, "usd"))],
    [copy.limits.apps, l.apps == null ? unl(copy) : n(l.apps)],
    [copy.limits.seats, l.seats == null ? unl(copy) : n(l.seats)],
    [copy.limits.data_history, n(`${l.data_history_days} ${u.days}`)],
    [copy.features.payments, check],
    [copy.features.subscriptions, check],
    [copy.features.crm, check],
    [copy.features.traffic, check],
  ]
}

export function PlanCard({ plan, interval, copy, cta }) {
  const yearCents = plan.prices.year?.amount_cents
  const monthCents = interval === "year" && yearCents != null
    ? Math.round(yearCents / 12)
    : plan.prices.month?.amount_cents
  const currency = (plan.prices.month || plan.prices.year)?.currency || "usd"

  return (
    <div
      className={`relative flex flex-col rounded-2xl border p-6 sm:p-7 ${
        plan.popular
          ? "border-brand-500/70 bg-gradient-to-b from-brand-500/10 to-transparent shadow-xl shadow-brand-500/10"
          : "border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60"
      }`}
    >
      {plan.popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-500 px-4 py-1 text-xs font-bold uppercase tracking-wide text-gray-900">
          {copy.popular}
        </span>
      )}

      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{plan.name}</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{plan.tagline}</p>

      <div className="mt-6">
        <span className="text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          {plan.free ? money(0, currency) : money(monthCents, currency)}
        </span>
        <span className="text-base font-medium text-gray-400">{copy.units.per_mo}</span>
      </div>
      <p className="mt-2 h-4 text-sm text-gray-400 dark:text-gray-500">
        {!plan.free && yearCents != null && copy.billed.replace("%{total}", money(yearCents, currency))}
      </p>

      <hr className="my-6 border-gray-200 dark:border-gray-800" />

      <dl className="space-y-3 text-sm">
        {limitRows(plan, copy).map(([label, value]) => (
          <div key={label} className="flex items-center justify-between">
            <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-8 flex-1 flex flex-col justify-end">{cta}</div>
    </div>
  )
}

export function IntervalToggle({ interval, setInterval, copy }) {
  const annual = interval === "year"
  return (
    <div className="mb-10 flex items-center justify-center gap-3 text-sm">
      <span className={annual ? "text-gray-400" : "font-semibold text-gray-900 dark:text-gray-100"}>{copy.monthly}</span>
      <button
        type="button"
        role="switch"
        aria-checked={annual}
        onClick={() => setInterval(annual ? "month" : "year")}
        className={`relative h-6 w-11 rounded-full transition-colors ${annual ? "bg-brand-500" : "bg-gray-300 dark:bg-gray-700"}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${annual ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
      <span className={annual ? "font-semibold text-gray-900 dark:text-gray-100" : "text-gray-400"}>{copy.annual}</span>
      <span className="rounded-full border border-brand-500/40 px-2.5 py-0.5 text-xs font-semibold text-brand-600 dark:text-brand-400">{copy.save}</span>
    </div>
  )
}
